import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DateTime } from "luxon";
import { exportToCsv } from "../../../utils/exportCsv";

const formatTimestamp = (isoString) => {
  if (!isoString) return "Unknown time";
  const dt = DateTime.fromISO(isoString);
  return dt.isValid ? dt.toFormat("dd LLL yyyy, HH:mm") : "Unknown time";
};

const CALL_STATUS_LABEL = {
  dial_no_answer: "No answer",
  dial_busy: "Line busy",
  went_to_voicemail: "Went to voicemail",
};

const CRITERION_LABEL = {
  communication: "Communication",
  relevance: "Relevance",
  experience: "Experience",
  role_fit: "Role Fit",
  red_flags: "Red Flags",
};

const AttemptStatus = ({ result }) => {
  if (result.interview_rating != null) {
    return <Chip label="Interview Complete" size="small" color="success" />;
  }
  if (CALL_STATUS_LABEL[result.call_status]) {
    return <Chip label="Unsuccessful" size="small" color="error" />;
  }
  if (result.outcome === "followup_with_time" || result.outcome === "followup_no_time") {
    return <Chip label="Follow-up requested" size="small" color="warning" />;
  }
  return <Chip label="Logged" size="small" />;
};

const ScreeningDetailDialog = ({ open, onClose, candidateName, results = [] }) => {
  const totalAttempts = results.length;

  const handleDownload = () => {
    const rows = results.map((result, index) => [
      totalAttempts - index,
      formatTimestamp(result.created_at),
      result.interview_rating != null ? "Interview Complete" : CALL_STATUS_LABEL[result.call_status] || result.outcome || "Logged",
      result.interview_rating ?? "",
      result.call_duration ?? "",
      result.call_summary || "",
      result.disqualification_reason || "",
      result.call_recording_link || "",
      result.transcript || "",
    ]);
    exportToCsv(
      `screening-history-${(candidateName || "candidate").replace(/\s+/g, "-")}.csv`,
      ["Attempt", "Date", "Status", "Rating", "Duration (s)", "Summary", "Reason", "Recording", "Transcript"],
      rows
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6">Screening History</Typography>
          <Typography variant="body2" color="textSecondary">
            {candidateName}
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={handleDownload} size="small" disabled={totalAttempts === 0} title="Download">
            <FileDownloadIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {totalAttempts === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No screening attempts yet.
          </Typography>
        ) : (
          <Stack spacing={2} divider={<Divider />}>
            {results.map((result, index) => {
              const isComplete = result.interview_rating != null;
              const perCriterion = result.interview_rating_details?.per_criterion || null;

              return (
                <Box key={index}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Typography variant="subtitle2">
                      Attempt {totalAttempts - index}
                    </Typography>
                    <AttemptStatus result={result} />
                  </Box>
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    {formatTimestamp(result.created_at)}
                    {result.attempt_count != null && ` · attempt #${result.attempt_count}`}
                    {result.call_duration != null && ` · ${result.call_duration}s`}
                  </Typography>

                  {isComplete && (
                    <Box mb={1}>
                      <Typography variant="body2">
                        Rating: {result.interview_rating}
                      </Typography>
                      {perCriterion && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {Object.entries(perCriterion)
                            .map(([key, value]) => `${CRITERION_LABEL[key] || key}: ${value}`)
                            .join(" · ")}
                        </Typography>
                      )}
                      {result.call_summary && (
                        <Typography variant="body2" mt={0.5}>
                          {result.call_summary}
                        </Typography>
                      )}
                      {result.call_recording_link && (
                        <a
                          href={result.call_recording_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#2563eb", textDecoration: "underline" }}
                        >
                          Listen to recording
                        </a>
                      )}
                    </Box>
                  )}

                  {!isComplete && CALL_STATUS_LABEL[result.call_status] && (
                    <Typography variant="body2" color="error">
                      {CALL_STATUS_LABEL[result.call_status]}
                    </Typography>
                  )}

                  {!isComplete && result.disqualification_reason && (
                    <Typography variant="body2" color="error">
                      Reason: {result.disqualification_reason}
                    </Typography>
                  )}

                  {!isComplete && result.callback_time && (
                    <Typography variant="body2">
                      Callback requested: {result.callback_time}
                    </Typography>
                  )}

                  {result.transcript && (
                    <Box
                      mt={1}
                      sx={{
                        maxHeight: 160,
                        overflowY: "auto",
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography variant="caption" component="pre" sx={{ whiteSpace: "pre-wrap", m: 0 }}>
                        {result.transcript}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScreeningDetailDialog;
